/**
 * Alle sichtbaren Texte der Oberfläche (Regel 7). Kein String steht in einer
 * Komponente. Du-Form, keine Ausrufezeichen, keine Geviertstriche.
 *
 * Später kommt hier eine zweite Sprache daneben, deshalb liegt die Datei im Kern
 * und nicht in apps/web.
 */
export const de = {
  app: {
    name: "AInkaufsagent",
    shortName: "AInkauf",
    tagline: "Die Einkaufsliste für die ganze Familie",
  },
  greeting: {
    morning: "Guten Morgen",
    day: "Hallo",
    evening: "Guten Abend",
    night: "Noch wach",
  },
  common: {
    save: "Speichern",
    cancel: "Abbrechen",
    delete: "Löschen",
    undo: "Rückgängig",
    retry: "Nochmal versuchen",
    close: "Schließen",
    back: "Zurück",
    loading: "Lädt",
    search: "Suchen",
    settings: "Einstellungen",
  },
  sync: {
    offline: "Offline. Deine Änderungen werden gespeichert und später übertragen.",
    online: "Verbunden",
    pending: (count: number) => (count === 1 ? "1 Änderung wartet" : `${count} Änderungen warten`),
    synced: "Alles übertragen",
    failed: "Übertragung fehlgeschlagen",
  },
  auth: {
    title: "Anmelden",
    subtitle: "Mit Apple oder Google anmelden, danach der Familie beitreten.",
    apple: "Mit Apple anmelden",
    google: "Mit Google anmelden",
    signOut: "Abmelden",
    noProvider: "Für diese Instanz ist noch kein Anmeldeweg eingerichtet.",
  },
  family: {
    create: "Familie anlegen",
    join: "Familie beitreten",
    nameLabel: "Name der Familie",
    codeLabel: "Einladungscode",
    codeHint: "Sechs Zeichen, die du von jemandem aus der Familie bekommen hast.",
    members: "Mitglieder",
    someone: "Jemand",
  },
  lists: {
    title: "Listen",
    empty: "Noch keine Liste. Leg die erste an.",
    create: "Liste anlegen",
    rename: "Liste umbenennen",
    delete: "Liste löschen",
    deleteConfirm: (name: string) => `${name} wirklich löschen? Die Artikel darin verschwinden mit.`,
    nameLabel: "Name der Liste",
    namePlaceholder: "Zum Beispiel Wocheneinkauf",
    defaultName: "Einkauf",
    storeLabel: "Laden",
    noStore: "Kein Laden",
    allDone: "Alles erledigt",
    allDoneHint: "Schließ den Einkauf ab, dann wandert alles ins Archiv.",
  },
  items: {
    quickAddPlaceholder: "Artikel hinzufügen",
    quickAddOpen: "Artikel hinzufügen",
    quickAddClose: "Eingabe schließen",
    onlyOnSale: "Nur im Angebot",
    onlyOnSaleToggle: (name: string, an: boolean) => (an ? `${name}: nicht mehr nur im Angebot` : `${name}: nur im Angebot kaufen`),
    quickAddHint: "Menge und Einheit dürfen davor stehen, zum Beispiel 2 kg Kartoffeln.",
    empty: "Diese Liste ist leer.",
    emptyHint: "Tipp etwas ein oder sprich es einfach ein.",
    done: "Erledigt",
    doneCount: (count: number) => (count === 1 ? "1 erledigt" : `${count} erledigt`),
    openCount: (count: number) => (count === 1 ? "1 offen" : `${count} offen`),
    checked: "Abgehakt",
    unchecked: "Wieder offen",
    checkedBy: (name: string) => `von ${name}`,
    checkedToast: (name: string) => `${name} abgehakt`,
    /** Verteilung in die passende Liste (03.10.2026). */
    movedToList: (name: string, liste: string) => `${name} steht auf ${liste}`,
    movedMany: (count: number, liste: string) => `${count} Artikel stehen auf ${liste}`,
    movedSeveral: (count: number) => `${count} Artikel auf andere Listen verteilt`,
    nameLabel: "Artikel",
    quantityLabel: "Menge",
    unitLabel: "Einheit",
    noteLabel: "Notiz",
    withoutCategory: "Ohne Kategorie",
    recurring: "Immer wieder",
    favorite: "Als Favorit merken",
    photo: "Foto",
    photoAdd: "Foto aufnehmen",
    photoRemove: "Foto entfernen",
    photoWaiting: "Foto wartet auf Übertragung",
    recurringHint: "Bleibt nach dem Einkauf auf der Liste.",
    categoryLabel: "Kategorie",
    edit: "Artikel bearbeiten",
    deleteItem: "Artikel löschen",
    units: {
      stk: "Stück",
      g: "Gramm",
      kg: "Kilogramm",
      ml: "Milliliter",
      l: "Liter",
      pkg: "Packung",
      dose: "Dose",
      glas: "Glas",
      flasche: "Flasche",
      bund: "Bund",
      becher: "Becher",
    },
  },
  trip: {
    start: "Einkauf starten",
    finish: "Einkauf abschließen",
    finished: "Einkauf gespeichert",
    mode: "Einkaufsmodus",
    currentAisle: "Hier bist du",
    nextAisle: "Als Nächstes",
    unavailable: "Nicht da",
    unavailableHint: "Nach links wischen, wenn es den Artikel im Laden nicht gibt.",
    sum: "Summe",
    sumEstimated: "geschätzt",
    remaining: (count: number) => (count === 1 ? "noch 1 Artikel" : `noch ${count} Artikel`),
    leave: "Einkauf verlassen",
    store: "Laden",
    storeNone: "Ohne Laden",
    storeQuestion: "Wo kaufst du gerade ein?",
    storeHint: "Danach sortiert sich die Liste nach den Gängen dieses Ladens. Du musst nichts auswählen.",
    storeOwn: "Eure Läden",
    storeChains: "Ketten",
    storeChosen: (name: string) => `Sortiert nach ${name}`,
    screenAwake: "Bildschirm bleibt an",
    totalQuestion: "Was hat der Einkauf gekostet?",
    totalHint: "Kannst du auch weglassen. Der Betrag hilft später bei der Übersicht.",
    totalLabel: "Betrag in Euro",
    skipTotal: "Ohne Betrag abschließen",
    repeat: "Alles wieder auf die Liste",
    repeatOne: "Wieder kaufen",
    repeated: (count: number) => (count === 1 ? "1 Artikel zurück auf der Liste" : `${count} Artikel zurück auf der Liste`),
    receiptScan: "Bon fotografieren",
    receiptReading: "Bon wird gelesen",
    receiptHint: "Aus dem Bon übernimmt die App Betrag und Einzelpreise.",
    receiptFound: (count: number, amount: string) =>
      count === 1 ? `1 Position erkannt, Summe ${amount}` : `${count} Positionen erkannt, Summe ${amount}`,
    receiptFailed: "Der Bon war nicht lesbar. Tipp den Betrag einfach ein.",
    receiptRemove: "Bon verwerfen",
    lastTrip: (wann: string) => `Zuletzt eingekauft ${wann}`,
    someoneShopping: (name: string) => `${name} kauft gerade ein`,
    inCartOf: (name: string, item: string) => `${name} hat ${item} im Wagen`,
    finishedElsewhere: "Der Einkauf wurde auf einem anderen Telefon abgeschlossen. Was du jetzt noch abhakst, wird ein eigener Einkauf.",
  },
  history: {
    title: "Historie",
    empty: "Noch kein Einkauf abgeschlossen.",
    positions: (count: number) => (count === 1 ? "1 Position" : `${count} Positionen`),
    noAmount: "ohne Betrag",
  },
  spending: {
    title: "Ausgaben",
    thisMonth: "Diesen Monat",
    perMonth: "Letzte 12 Monate",
    perStore: "Nach Laden",
    budget: "Monatsbudget",
    budgetHint: "Leer lassen, wenn ihr ohne Budget plant.",
    budgetRemaining: (amount: string) => `noch ${amount}`,
    budgetOver: (amount: string) => `${amount} über dem Budget`,
    noData: "Noch keine Ausgaben erfasst. Sie entstehen, sobald du einen Einkauf mit Betrag abschließt.",
    estimated: "Geschätzte Summe der offenen Artikel",
    estimatedShort: (amount: string) => `ca. ${amount}`,
    estimatedUnknown: (count: number) => (count === 1 ? "1 Artikel ohne Preis" : `${count} Artikel ohne Preis`),
  },
  prices: {
    title: "Preisverlauf",
    empty: "Noch kein Preis bekannt.",
    emptyHint: "Preise entstehen, wenn du beim Abschließen eines Einkaufs den Bon fotografierst.",
    open: "Preisverlauf ansehen",
    lastPaid: (amount: string) => `zuletzt ${amount}`,
    cheapest: (store: string, percent: number) => `Bei ${store} zuletzt ${percent} Prozent günstiger`,
    noStore: "Ohne Laden",
    observations: (count: number) => (count === 1 ? "1 Preis beobachtet" : `${count} Preise beobachtet`),
    boughtCount: (count: number) => (count === 1 ? "1 Mal gekauft" : `${count} Mal gekauft`),
  },
  stores: {
    title: "Läden",
    empty: "Noch kein Laden. Mit einem Laden sortiert sich die Liste nach Gängen.",
    create: "Laden anlegen",
    nameLabel: "Name des Ladens",
    namePlaceholder: "Zum Beispiel Rewe Lindenthal",
    chainLabel: "Kette",
    chainHint: "Die Kette legt die Reihenfolge der Gänge fest. Die App lernt sie pro Filiale nach.",
    noChain: "Ohne Kette",
    isDefault: "Standardladen",
  },
  settings: {
    title: "Einstellungen",
    familySection: "Familie",
    storesSection: "Läden",
    pushSection: "Benachrichtigungen",
    syncSection: "Abgleich",
    inviteCode: "Einladungscode",
    inviteHint: "Wer diesen Code eingibt, landet in eurer Familie.",
    inviteShare: "Einladung teilen",
    inviteCopy: "Code kopieren",
    inviteCopied: "Kopiert",
    inviteValidUntil: (date: string) => `Gültig bis ${date}`,
    inviteMessage: (code: string, url: string) =>
      `Komm in unsere Einkaufsliste: ${url}?code=${code} (Code ${code})`,
    inviteNone: "Noch kein Einladungscode. Lege einen an, um jemanden einzuladen.",
    inviteCreate: "Einladungscode erzeugen",
    pushHint: "Meldet, wenn jemand etwas auf eine Liste setzt oder einkaufen geht. Gilt nur für dieses Gerät.",
    pushOn: "Benachrichtigungen an",
    pushOff: "Benachrichtigungen aus",
    pushEnable: "Einschalten",
    pushDisable: "Ausschalten",
    pushDenied: "Der Browser blockiert Benachrichtigungen. Das lässt sich nur in den Einstellungen des Browsers ändern.",
    pushUnsupported: "Dieser Browser kann keine Benachrichtigungen.",
    pushNotConfigured: "Für diese Instanz sind noch keine Schlüssel hinterlegt.",
    pushInstallFirst: "Auf dem iPhone geht das erst, wenn die App über Teilen auf dem Startbildschirm liegt.",
    localMode: "Ohne Anmeldung. Alles bleibt auf diesem Gerät.",
    version: "Fassung",
    syncNow: "Jetzt abgleichen",
    syncPending: (count: number) => (count === 1 ? "1 Änderung wartet auf Übertragung" : `${count} Änderungen warten auf Übertragung`),
    syncClean: "Alles übertragen",
    syncPhotos: (count: number) => (count === 1 ? "1 Foto wartet" : `${count} Fotos warten`),
    syncStuck: "Hängt beim Übertragen",
    syncStuckHint: "Diese Änderungen kommen nicht durch. Der Grund steht daneben.",
    syncAttempts: (count: number) => (count === 1 ? "1 Versuch" : `${count} Versuche`),
    syncReload: "Alles neu vom Server laden",
    syncReloaded: "Wird neu geladen",
    deviceSection: "Dieses Gerät",
    deviceFamily: "Familie",
    deviceLocal: "Gerätekennung",
    deviceAdopted: "Übernahme erledigt",
    deviceRows: (listen: number, artikel: number) => `${listen} Listen, ${artikel} Artikel geladen`,
    deviceMarks: "Zuletzt geholt",
    syncLastError: "Zuletzt gemeldet",
  },
  assistant: {
    start: "Sprechen",
    stop: "Fertig",
    listening: "Ich höre",
    thinking: "Einen Moment",
    failed: "Das hat nicht geklappt",
    offline: "Ohne Verbindung kann ich nicht zuhören. Die Aufnahme wartet.",
    nothingUnderstood: "Ich habe nichts verstanden.",
    preview: "Das habe ich verstanden",
    apply: "Übernehmen",
    discard: "Verwerfen",
    uncertain: "Unsicher",
    toList: (name: string) => `Auf die Liste ${name}`,
    addedTo: (count: number, name: string) => (count === 1 ? `1 Artikel auf ${name}` : `${count} Artikel auf ${name}`),
    checkAction: "Abhaken",
    removeAction: "Entfernen",
    createListAction: "Neue Liste",
    photoNote: "Zettel fotografieren",
    heard: "Gehört",
  },
  plan: {
    title: "Planen",
    tabWeek: "Woche",
    tabRecipes: "Rezepte",
    tabPantry: "Vorrat",
  },
  week: {
    title: "Wochenplan",
    today: "Heute",
    thisWeek: "Diese Woche",
    previous: "Woche davor",
    next: "Woche danach",
    addMeal: "Essen planen",
    addMealFor: (tag: string) => `Essen für ${tag}`,
    chooseRecipe: "Rezept wählen",
    freeText: "Oder etwas eintippen",
    freeTextPlaceholder: "Zum Beispiel Reste oder Essen gehen",
    servings: "Für wie viele",
    servingsOf: (count: number) => (count === 1 ? "für 1 Person" : `für ${count} Personen`),
    empty: "Für diese Woche ist noch nichts geplant.",
    emptyHint: "Tipp auf einen Tag und wähl ein Rezept.",
    remove: "Vom Plan nehmen",
    toList: "Woche auf die Liste",
    nothingPlanned: "Nichts geplant",
  },
  spaisekammer: {
    section: "Spaisekammer",
    intro: "Verbinde die Rezeptsammlung eurer Familie. Den Code findest du in der Spaisekammer unter Einstellungen, Verbundene Apps.",
    codeLabel: "Code",
    codePlaceholder: "ABC234",
    connect: "Verbinden",
    connecting: "Wird verbunden",
    connectedWith: (name: string) => `Verbunden mit ${name}`,
    recipeCount: (count: number) => (count === 1 ? "1 Rezept" : `${count} Rezepte`),
    lastSync: (wann: string) => `zuletzt abgeglichen ${wann}`,
    neverSynced: "noch nicht abgeglichen",
    syncNow: "Jetzt abgleichen",
    syncing: "Wird abgeglichen",
    disconnect: "Trennen",
    disconnectConfirm: "Verbindung trennen? Die Rezepte bleiben als eigene Kopien hier stehen.",
    disconnected: "Die Spaisekammer hat die Verbindung getrennt.",
    origin: "Spaisekammer",
    by: (name: string) => `von ${name}`,
    open: "In der Spaisekammer öffnen",
    readOnly: "Bearbeiten geht in der Spaisekammer.",
    search: "Rezept suchen",
    searchEmpty: "Kein Rezept passt dazu.",
    errorCode: "Der Code stimmt nicht oder ist abgelaufen.",
    errorOffline: "Die Spaisekammer ist gerade nicht erreichbar.",
    errorGeneric: "Das hat nicht geklappt. Versuch es noch einmal.",
    errorAlready: (name: string) => `Diese Familie ist schon mit ${name} verbunden.`,
    noWebhook: "Neue Rezepte kommen beim Öffnen von Planen an, nicht sofort.",
  },
  recipes: {
    title: "Rezepte",
    empty: "Noch kein Rezept.",
    emptyHint: "Tipp eins ein, fotografier es oder gib einen Link an.",
    create: "Rezept anlegen",
    edit: "Rezept bearbeiten",
    delete: "Rezept löschen",
    deleteConfirm: (name: string) => `${name} wirklich löschen? Die Zutaten verschwinden mit.`,
    nameLabel: "Name",
    namePlaceholder: "Zum Beispiel Linsensuppe",
    servingsLabel: "Für wie viele Personen",
    noteLabel: "Notiz",
    sourceLabel: "Quelle",
    ingredients: "Zutaten",
    ingredientCount: (count: number) => (count === 1 ? "1 Zutat" : `${count} Zutaten`),
    addIngredient: "Zutat hinzufügen",
    ingredientPlaceholder: "Zum Beispiel 250 g Nudeln",
    staple: "Immer im Haus",
    stapleHint: "Salz, Öl, Pfeffer. Kommt nie auf die Liste.",
    toList: "Zutaten auf die Liste",
    /** Der Warenkorb an einer einzelnen Zutatenzeile (21.09.2026). */
    ingredientToList: (name: string) => `${name} auf die Liste`,
    ingredientOnList: (name: string) => `${name} steht schon auf der Liste`,
    ingredientAdded: (name: string, liste: string) => `${name} steht auf ${liste}`,
    plan: "Einplanen",
    importTitle: "Rezept übernehmen",
    importHint: "Tipp den Text ein, füg einen Link ein oder fotografier das Rezept.",
    importText: "Text oder Link",
    importTextPlaceholder: "Rezepttext einfügen oder https://...",
    importPhoto: "Rezept fotografieren",
    importReading: "Rezept wird gelesen",
    importFailed: "Daraus konnte ich kein Rezept lesen. Tipp es lieber selbst ein.",
    importFound: (name: string, count: number) =>
      count === 1 ? `${name} mit 1 Zutat erkannt` : `${name} mit ${count} Zutaten erkannt`,
  },
  pantry: {
    title: "Vorrat",
    empty: "Noch kein Vorrat erfasst.",
    emptyHint: "Trag ein, was ihr auf Vorrat habt. Dann zieht die Wochenplanung es ab.",
    add: "Vorrat hinzufügen",
    edit: "Vorrat bearbeiten",
    nameLabel: "Was",
    namePlaceholder: "Zum Beispiel Mehl",
    quantityLabel: "Bestand",
    minLabel: "Mindestbestand",
    minHint: "Darunter schlägt die App vor, nachzukaufen. Leer lassen, wenn es egal ist.",
    locationLabel: "Wo",
    locations: {
      vorrat: "Vorratsschrank",
      kuehlschrank: "Kühlschrank",
      gefrierer: "Gefrierfach",
    },
    lowTitle: "Geht zur Neige",
    lowCount: (count: number) => (count === 1 ? "1 Vorrat geht zur Neige" : `${count} Vorräte gehen zur Neige`),
    lowToList: "Nachkaufen",
    restocked: (count: number) => (count === 1 ? "1 Vorrat aufgefüllt" : `${count} Vorräte aufgefüllt`),
    plus: "Mehr",
    minus: "Weniger",
  },
  shops: {
    /** Eigenes Objekt, damit sich ein ShopKey sauber darauf abbilden lässt. */
    namen: {
      asiamarkt: "Asiamarkt",
      baumarkt: "Baumarkt",
      drogerie: "Drogerie",
    },
    missingTitle: (laden: string) => `Keine Liste für den ${laden}`,
    missingHint: (name: string, laden: string) => `${name} gehört eher in den ${laden} als in den Supermarkt. Eine Liste dafür hast du noch nicht.`,
    createList: (laden: string) => `Liste ${laden} anlegen`,
    keepDefault: (liste: string) => `Auf ${liste} lassen`,
    created: (liste: string) => `Liste ${liste} angelegt`,
  },
  planning: {
    preview: "Das kommt auf die Liste",
    covered: (count: number) => (count === 1 ? "1 Zutat ist im Vorrat" : `${count} Zutaten sind im Vorrat`),
    staples: "Immer im Haus, kommt nicht auf die Liste",
    nothingMissing: "Alles da. Es fehlt nichts.",
    confirm: "Auf die Liste setzen",
    added: (count: number) => (count === 1 ? "1 Artikel auf der Liste" : `${count} Artikel auf der Liste`),
    fromRecipes: (namen: string) => `Aus ${namen}`,
    chooseHint: "Tipp an, was mit soll.",
    goesTo: (liste: string) => `kommt auf ${liste}`,
    chosen: (gewaehlt: number, gesamt: number) => `${gewaehlt} von ${gesamt} gewählt`,
    all: "Alle",
    none: "Keine",
    targetList: "Auf welche Liste",
    confirmCount: (count: number) => (count === 1 ? "1 Artikel auf die Liste" : `${count} Artikel auf die Liste`),
    nothingChosen: "Nichts gewählt.",
  },
  nav: {
    lists: "Listen",
    plan: "Planen",
    spending: "Ausgaben",
    settings: "Einstellungen",
  },
  errors: {
    generic: "Da ist etwas schiefgegangen.",
    offlineAction: "Das geht nur mit Verbindung.",
    notFound: "Nicht gefunden.",
  },
  pwa: {
    updateAvailable: "Neue Version verfügbar",
    reload: "Jetzt neu laden",
    installed: "AInkaufsagent ist installiert",
  },
} as const;

export type Translations = typeof de;
